<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import FiveChart from './FiveChart.vue';
import ContentsTitle from './ContentsTitle.vue';
import { fiveElementColors } from './FiveChart';
import { transformYearlyFortuneText } from '@/composables/splitSentence';

const props = defineProps({
  userName: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 데이터 접근 편의를 위한 computed
const 사주정보 = computed(() => props.data?.사주정보 || {});
const 전체요약 = computed(() => props.data?.전체요약 || {});

// 특징요약을 '. ' 기준으로 줄바꿈
const 특징요약Lines = computed(() => {
  const text = 전체요약.value?.특징요약 || '';
  return text.split('. ').join('.\n');
});

// 오행 차트 데이터
const 차트Data = computed(() => 전체요약.value?.오행분포 || {});

// 변화요약
const 변화요약 = computed(() => 전체요약.value?.오행분포?.변화요약 || []);

const getElementColor = (text) => {
  const found = ['목', '화', '토', '금', '수'].find((el) => text.includes(el));
  return found ? fiveElementColors[found] : '#333';
};
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼: ① 2026년 나의 흐름은 어떻게 바뀔까요? -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="1"
          text="2026년 나의 흐름은 어떻게 바뀔까요?"
          :isMobile="isMobile"
        />
        <div class="detailed-analysis-box yearly-fortune-wrapper">
          <div class="title-box">
            <div class="title">
              '{{ 사주정보?.이름 || '-' }}'님의
              <span class="title__underline"
                >신년운세 분석
                <div class="underline-box"></div>
              </span>
            </div>
            <div class="subtitle">
              같은 해라도 사주가 지닌 구조에 따라 에너지가 움직이는 방향은 다르게 나타납니다.
            </div>
            <div class="subtitle">
              이 화면에서는 2026년 병오년을 맞아<br />
              내 사주에 어떤 변화가 생기는지 먼저 흐름부터 살펴봅니다.
            </div>
          </div>
          <!-- 4개 이미지 연결 구조 -->
          <section class="flow-section">
            <div class="flow-layout">
              <!-- 1. 갈림길 캐릭터 -->
              <div class="flow-layout__crossroads">
                <img src="@manseImages/yearly-fortune/img_year_0101.png" alt="" />
              </div>

              <!-- 2. 방향 박스 -->
              <div class="flow-layout__direction-box">
                {{ 전체요약?.방향 }}
              </div>

              <!-- 3. 감정 + 화살표 + 이모지 -->
              <div class="flow-layout__emotion-row">
                <img
                  class="flow-layout__heart-img"
                  src="@manseImages/yearly-fortune/img_year_0102.png"
                  alt=""
                />
                <img
                  class="flow-layout__arrow"
                  src="@manseImages/yearly-fortune/img_year_0105.png"
                  alt=""
                />
                <span class="flow-layout__emoji">{{ 전체요약?.이모지 }}</span>
              </div>

              <!-- 4. 삼각형 포인터 + 감정신호 -->
              <div v-if="전체요약?.감정신호" class="flow-layout__signal">
                <div class="flow-layout__signal-pointer"></div>
                <div class="flow-layout__signal-box">
                  {{ 전체요약?.감정신호 }}
                </div>
              </div>

              <!-- 5. 핵심메시지 + 우체통 -->
              <div class="flow-layout__message-row">
                <div class="flow-layout__message-bubble">
                  {{ 전체요약?.핵심메시지 }}
                </div>
                <img
                  class="flow-layout__mailbox-img"
                  src="@manseImages/yearly-fortune/img_year_0103.png"
                  alt=""
                />
              </div>

              <!-- 6. 스피커 + 설명 -->
              <div class="flow-layout__explain-row">
                <img
                  class="flow-layout__speaker-img"
                  src="@manseImages/yearly-fortune/img_year_0104.png"
                  alt=""
                />
                <div class="flow-layout__explain-bubble">
                  {{ 전체요약?.설명 }}
                </div>
              </div>

              <!-- 7. 특징요약 -->
              <div class="flow-layout__summary-text">
                {{ 특징요약Lines }}
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼: 전체 너비 섹션들 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="yearly-guide" :class="{ mobile: isMobile }">
          <section class="yearly-guide__section">
            <div class="yearly-guide__top" :class="{ mobile: isMobile }">
              <div class="yearly-guide__title">
                <span class="yearly-guide__title-icon yearly-guide__title-icon--triangle">⭐</span>
                <span class="box-title__txt">&nbsp;오행의 흐름 변화</span>
              </div>
              <div class="box-body">
                <span class="yearly-guide__explain-txt">
                  같은 해라도 사주가 지닌 구조에 따라
                  <span class="point">에너지가 움직이는 방향</span>은 다르게 나타납니다.<br />
                </span>
                <span class="yearly-guide__explain-txt">
                  2026년 병오년을 맞아 내 사주에 어떤 변화가 생기는지 사주원국의 오행과<br />
                  변화된 오행의 흐름을 살펴봅니다.
                </span>
              </div>
              <FiveChart :data="차트Data" />
            </div>
          </section>

          <section class="yearly-guide__section">
            <div class="yearly-guide__footer">
              <div class="yearly-guide__title">
                <span class="box-title__txt">어떻게 봐야 하죠?</span>
              </div>
              <div class="yearly-guide__footer-box">
                <div
                  v-for="(item, idx) in 변화요약"
                  :key="idx"
                  class="footer-box__item"
                  :style="{ color: getElementColor(item) }"
                >
                  {{ item }}
                </div>
              </div>
              <div class="box-body">
                <span
                  class="yearly-guide__explain-txt"
                  v-html="transformYearlyFortuneText(전체요약?.오행분포?.오행변화)"
                >
                </span>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
@import '@/assets/nangman_style/_analysis_main_color.scss';

.title-box .title {
  &__underline {
    position: relative;
    display: inline-block;

    .underline-box {
      position: absolute;
      bottom: 3px;
      left: 0;
      transform: translateX(-3%);
      width: 108%;
      height: 8px;
      background: rgba(190, 51, 49, 0.15);
      z-index: -1;
    }
  }
}

.flow-section {
  width: 100%;
  min-width: 500px;
  margin-top: 30px;
  margin-bottom: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(190, 51, 49, 0.05);
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-bottom: 0;
    min-width: 100%;
  }
}

.flow-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 500px;
  padding: 32px;

  &__crossroads {
    width: 100%;
    max-width: 360px;

    img {
      width: 100%;
      height: auto;
    }
  }

  &__direction-box {
    width: 100%;
    max-width: 360px;
    margin-top: 0px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #be3331;
    border-radius: 10px;
    text-align: center;
    font-family: Pretendard;
    font-weight: 700;
    font-size: 15px;
    line-height: 24px;
    color: #333;
  }

  &__emotion-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-top: 40px;
    width: 100%;
    max-width: 360px;
  }

  &__heart-img {
    width: 99px;
    height: auto;

    @media (max-width: 480px) {
      width: 75px;
    }
  }

  &__arrow {
    width: 42px;
    height: auto;
  }

  &__emoji {
    font-size: 60px;
    line-height: 1;

    @media (max-width: 480px) {
      font-size: 46px;
    }
  }

  &__signal {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 12px;
  }

  &__signal-pointer {
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-bottom: 14px solid #fff;
  }

  &__signal-box {
    width: 100%;
    max-width: 360px;
    padding: 6px 16px;
    background: #fff;
    border-radius: 12px;
    text-align: center;
    font-family: Pretendard;
    font-weight: 700;
    font-size: 15px;
    line-height: 24px;
    color: #333;
  }

  &__message-row {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 40px;
    width: 100%;
    max-width: 360px;
    justify-content: flex-end;
  }

  &__message-bubble {
    position: relative;
    flex: 0 1 auto;
    padding: 8px 15px;
    background: #be3331;
    border-radius: 12px;
    color: #fff;
    font-family: Pretendard;
    font-weight: 700;
    font-size: 15px;
    line-height: 24px;

    &::after {
      content: '';
      position: absolute;
      right: -10px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-left: 12px solid #be3331;
    }
  }

  &__mailbox-img {
    width: 100px;
    height: auto;

    @media (max-width: 480px) {
      width: 80px;
    }
  }

  &__explain-row {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 40px;
    width: 100%;
    max-width: 360px;
  }

  &__speaker-img {
    width: 102px;
    height: auto;
    flex-shrink: 0;

    @media (max-width: 480px) {
      width: 75px;
    }
  }

  &__explain-bubble {
    position: relative;
    flex: 0 1 auto;
    padding: 8px 12px;
    background: #fff;
    border-radius: 12px;
    font-family: Pretendard;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.4;
    color: #333;

    &::before {
      content: '';
      position: absolute;
      left: -10px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-right: 12px solid #fff;
    }

    @media (max-width: 480px) {
      font-size: 15px;
    }
  }

  &__summary-text {
    margin-top: 24px;
    width: 100%;
    max-width: 360px;
    text-align: center;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    white-space: pre-wrap;
    word-break: keep-all;
  }

  @media (max-width: 480px) {
    padding: 16px 20px;
  }
}

.yearly-guide__explain-txt {
  color: var(--Gray-800, #333);
  text-align: left;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  .point {
    color: #be3331;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }
}

.yearly-guide {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;
  gap: 30px;

  &__section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    align-self: stretch;
  }

  &__top {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    align-self: stretch;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 8px;

    &-icon {
      font-size: 20px;
      font-weight: 700;
      flex-shrink: 0;

      &--triangle {
        color: var(--orange-300-main, #eb4c10);
      }
    }
  }

  &__footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    &-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      margin-bottom: 8px;
      width: fit-content;
      padding: 24px 40px;
      border-radius: 12px;
      background: rgba(190, 51, 49, 0.05);

      .footer-box__item {
        font-family: Pretendard;
        font-size: 14px;
        font-weight: 700;
        line-height: 22px;
      }
    }
  }

  &.mobile {
    padding-bottom: 0px;
  }
}

.box-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}
</style>
